import type { PlacementProgress } from "./journalOrderStatus";

// Отслеживаемые оформления из Быстрой корзины (CAB-782) живут в localStorage:
// заказ уходит поставщику асинхронно, и его отказ может прийти после того, как
// страницу перезагрузили. Храним только ключи и увиденное состояние строки —
// сами данные позиции при восстановлении перечитываются из журнала.

const STORAGE_KEY = "eplex.quickCart.placements";

export interface StoredPendingPlacement extends PlacementProgress {
  thash: string;
}

export interface StoredQuickCartPlacements {
  /** Заявка принята, итог ещё не известен. */
  pending: StoredPendingPlacement[];
  /** Оформление закончилось отказом: показываем в корзине, пока строку не повторят или не удалят. */
  failed: string[];
}

export const EMPTY_QUICK_CART_PLACEMENTS: StoredQuickCartPlacements = { pending: [], failed: [] };

function isPendingEntry(value: unknown): value is StoredPendingPlacement {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.thash === "string" &&
    entry.thash.length > 0 &&
    typeof entry.seenStatus === "number" &&
    (entry.seenAttempt === null || typeof entry.seenAttempt === "number") &&
    typeof entry.seenAt === "number"
  );
}

function isThash(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

export function loadQuickCartPlacements(): StoredQuickCartPlacements {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { pending: [], failed: [] };
    const parsed = JSON.parse(raw) as Partial<Record<keyof StoredQuickCartPlacements, unknown>>;
    const pending = Array.isArray(parsed.pending) ? parsed.pending.filter(isPendingEntry) : [];
    const failed = Array.isArray(parsed.failed) ? parsed.failed.filter(isThash) : [];
    return { pending, failed };
  } catch (error) {
    console.error("Failed to load quick cart placements: ", error);
    return { pending: [], failed: [] };
  }
}

export function saveQuickCartPlacements(placements: StoredQuickCartPlacements): void {
  try {
    if (placements.pending.length === 0 && placements.failed.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(placements));
  } catch (error) {
    console.error("Failed to save quick cart placements: ", error);
  }
}
